import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

let injectedId = null;

function injectGtag(measurementId) {
  if (!measurementId || injectedId === measurementId) return;
  injectedId = measurementId;

  // Clean up any previously injected gtag scripts (in case ID changed at runtime)
  document.querySelectorAll('script[data-ga-inject="1"]').forEach((el) => el.remove());

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view disabled — SPA route changes are tracked below on route change
  window.gtag("config", measurementId, { send_page_view: false });

  const s = document.createElement("script");
  s.async = true;
  s.setAttribute("data-ga-inject", "1");
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);
}

/**
 * Sends a page_view to GA4 whenever the SPA route changes.
 * Reads the measurement ID from site settings, so the admin can change it live.
 */
export function useAnalytics() {
  const location = useLocation();
  const { settings } = useSettings();
  const measurementId = settings?.ga_measurement_id || "";
  const lastPath = useRef(null);

  useEffect(() => {
    if (!measurementId) return;
    injectGtag(measurementId);
  }, [measurementId]);

  useEffect(() => {
    if (!measurementId || typeof window === "undefined" || typeof window.gtag !== "function") return;
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: measurementId,
    });
  }, [location.pathname, location.search, measurementId]);
}

/**
 * Fire a custom GA4 event. Safe to call even if gtag hasn't loaded.
 * Usage: trackEvent('purchase', { transaction_id, value, currency, items });
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const measurementId = injectedId || (window.__gaMeasurementId || "");
  const send_to = params.send_to || measurementId;
  const payload = { ...params };
  if (send_to) payload.send_to = send_to;
  window.gtag("event", name, payload);
}
